import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { Badge } from '../ui/badge';
import { FeatureTag } from '../ui/feature-tag';
import { formatDueDate } from '../../utils/taskUtils';
import './CompletedTasksSection.css';

const CompletedTasksSection = ({ completedTasks, projectId, onTaskClick, variant }) => {
    const isReport = variant === 'report';
    const [isCollapsed, setIsCollapsed] = useState(true);

    const getDaysSinceCompletion = (updatedAt) => {
        if (!updatedAt) return 0;
        const completed = new Date(updatedAt);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        completed.setHours(0, 0, 0, 0);
        const diffTime = today - completed;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const formatDays = (days) => {
        if (days === 0) return 'Today';
        if (days === 1) return '1 day ago';
        return `${days} days ago`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusBadgeVariant = (status) => {
        if (status === 'released') return 'success';
        if (status === 'completed') return 'primary';
        return 'secondary';
    };

    if (!completedTasks || completedTasks.length === 0) {
        return (
            <div className="completed-tasks-section">
                <div 
                    className="section-header" 
                    onClick={() => setIsCollapsed(!isCollapsed)} 
                    style={{ cursor: 'pointer' }}
                >
                    <h2>Completed Tasks (This Week)</h2>
                    <button 
                        className="stats-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsCollapsed(!isCollapsed);
                        }}
                        aria-label={isCollapsed ? "Expand" : "Collapse"}
                    >
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
                <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="empty-state success">
                        <p>✅ No tasks completed this week.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`completed-tasks-section ${isReport ? 'completed-tasks-section-report' : ''}`}>
            <div 
                className="section-header" 
                onClick={() => setIsCollapsed(!isCollapsed)} 
                style={{ cursor: 'pointer' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <h2>Completed Tasks (This Week)</h2>
                    <Badge variant="success">
                        {completedTasks.length} {completedTasks.length === 1 ? 'task' : 'tasks'} completed
                    </Badge>
                </div>
                <button 
                    className="stats-toggle-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsCollapsed(!isCollapsed);
                    }}
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                >
                    {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </button>
            </div>
            <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="table-container">
                <table className="completed-tasks-table">
                    <thead>
                        <tr>
                            <th>Task</th>
                            <th>Status</th>
                            <th>Completed By</th>
                            <th>Completed Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {completedTasks.map((task) => {
                            const daysSince = getDaysSinceCompletion(task.updated_at);
                            return (
                                <tr
                                    key={task.id}
                                    className={`completed-task-row ${!isReport && onTaskClick ? 'completed-task-row-clickable' : ''}`}
                                    onClick={!isReport && onTaskClick ? () => onTaskClick({
                                        id: task.id,
                                        title: task.title,
                                        display_id: task.display_id,
                                        description: task.description,
                                        status: task.status,
                                        feature_tag: task.feature_tag,
                                        created_at: task.created_at,
                                        updated_at: task.updated_at
                                    }) : undefined}
                                >
                                    <td>
                                        <div className="task-info">
                                            <div className="task-title">{task.title}</div>
                                            <div className="task-id">{task.display_id}</div>
                                            {task.feature_tag && (() => {
                                                const dueDateInfo = task.feature_tag.due_date 
                                                    ? formatDueDate(task.feature_tag.due_date) 
                                                    : null;
                                                const tagName = dueDateInfo 
                                                    ? `${task.feature_tag.name} (${dueDateInfo.text})`
                                                    : task.feature_tag.name;
                                                return (
                                                    <FeatureTag
                                                        name={tagName}
                                                        color={task.feature_tag.color || '#6366f1'}
                                                        size="small"
                                                    />
                                                );
                                            })()}
                                        </div>
                                    </td>
                                    <td>
                                        <Badge variant={getStatusBadgeVariant(task.status)}>
                                            {task.status}
                                        </Badge>
                                    </td>
                                    <td>
                                        <div className="completed-by">
                                            {task.assigned_to ? (
                                                <span className="assignee-name">
                                                    {task.assigned_to.display_name || task.assigned_to.email || 'Unknown'}
                                                </span>
                                            ) : (
                                                <span className="assignee-name unassigned">Unassigned</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="completion-date">
                                            <div className="date-value">{formatDate(task.updated_at)}</div>
                                            <div className="date-relative">
                                                {formatDays(daysSince)}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
};

export default CompletedTasksSection;

